import { createContext, useContext, useMemo, type ReactNode } from "react";
import { Effect } from "effect";
import { Atom, type AsyncResult } from "effect/unstable/reactivity";
import { useAtomMount, useAtomValue } from "@effect/atom-react";
import type { FrontendRoot } from "../root-owner.ts";
import { TextShimmers, type ShimmerSession } from "./shimmer-service.ts";
import type { ShimmerSnapshot } from "./shimmer-model.ts";

export interface ShimmerRoot { readonly atoms: FrontendRoot<TextShimmers>["atoms"] }
const ShimmerContext = createContext<ShimmerRoot | undefined>(undefined);
export const ShimmerProvider = ({ value, children }: { readonly value: ShimmerRoot; readonly children: ReactNode }) => <ShimmerContext.Provider value={value}>{children}</ShimmerContext.Provider>;
export function useTextShimmer(active: boolean): boolean {
  const root = useContext(ShimmerContext);
  const sessionAtom = useMemo<Atom.Atom<AsyncResult.AsyncResult<ShimmerSession | undefined>>>(() => root
    ? root.atoms.atom(Effect.gen(function* () { return yield* (yield* TextShimmers).open(active); })).pipe(Atom.setIdleTTL(0))
    : Atom.make(Effect.succeed(undefined)), [root]);
  const opened = useAtomValue(sessionAtom), session = opened._tag === "Success" ? opened.value : undefined;
  const selection = useMemo(() => root && session ? root.atoms.atom(session.receive(active)).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.void), [root, session, active]);
  useAtomMount(selection);
  const fallback = useMemo(() => Atom.make(Effect.succeed<ShimmerSnapshot>({ active, run: active })), [active]);
  const observed = useAtomValue(session?.snapshot ?? fallback);
  return active || (observed._tag === "Success" && observed.value.run);
}
